/**
 * Footer - Responsive footer with links
 */
import { Link } from 'react-router-dom'
import { Globe, Twitter, Linkedin, Github, Mail } from 'lucide-react'
import { Separator } from '../ui/separator'

const footerLinks = {
  product: [
    { label: 'Features', href: '/#features' },
    { label: 'Pricing', href: '/#pricing' },
    { label: 'Security', href: '/#security' },
    { label: 'Roadmap', href: '/#roadmap' },
  ],
  company: [
    { label: 'About', href: '#' },
    { label: 'Blog', href: '#' },
    { label: 'Careers', href: '#' },
    { label: 'Contact', href: '#' },
  ],
  resources: [
    { label: 'Documentation', href: '#' },
    { label: 'API Reference', href: '#' },
    { label: 'Partners', href: '#' },
    { label: 'Status', href: 'https://status.mzanzi.cloud' },
  ],
  legal: [
    { label: 'Privacy', href: '#' },
    { label: 'Terms', href: '#' },
    { label: 'Cookie Policy', href: '#' },
  ],
}

const socials = [
  { icon: Twitter, href: 'https://twitter.com/mzanzicloud', label: 'Twitter' },
  { icon: Linkedin, href: 'https://linkedin.com/company/mzanzi', label: 'LinkedIn' },
  { icon: Github, href: 'https://github.com/sadhac/mzanzi', label: 'GitHub' },
  { icon: Mail, href: 'mailto:hello@mzanzi.cloud', label: 'Email' },
]

function FooterBrand() {
  return (
    <div className="col-span-2 md:col-span-4 lg:col-span-1">
      <Link to="/" className="flex items-center gap-2 mb-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-blue-600">
          <Globe className="h-4 w-4 text-white" />
        </div>
        <span className="text-lg font-bold">Mzanzi</span>
      </Link>
      <p className="text-sm text-muted-foreground mb-4 max-w-[280px]">
        AI-native multi-vendor MSP platform for Pan-African cloud services.
      </p>
      <div className="flex gap-3">
        {socials.map((social) => (
          <a
            key={social.label}
            href={social.href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted hover:bg-primary hover:text-primary-foreground transition-colors"
            aria-label={social.label}
          >
            <social.icon className="h-4 w-4" />
          </a>
        ))}
      </div>
    </div>
  )
}

function FooterLinkColumn({ title, links }: { title: string; links: { label: string; href: string }[] }) {
  return (
    <div>
      <h3 className="font-semibold mb-3">{title}</h3>
      <ul className="space-y-2.5">
        {links.map((link) => (
          <li key={link.href}>
            {link.href.startsWith('http') ? (
              <a
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {link.label}
              </a>
            ) : (
              <Link
                to={link.href}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {link.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

export function Footer() {
  return (
    <footer className="border-t bg-muted/30">
      <div className="container px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 lg:gap-12">
          <FooterBrand />
          <FooterLinkColumn title="Product" links={footerLinks.product} />
          <FooterLinkColumn title="Company" links={footerLinks.company} />
          <FooterLinkColumn title="Resources" links={footerLinks.resources} />
          <FooterLinkColumn title="Legal" links={footerLinks.legal} />
        </div>

        <Separator className="my-8" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} Mzanzi Cloud. All rights reserved.</p>
          <p className="flex items-center gap-1">
            Made with <span className="text-red-500">❤</span> in Africa 🌍
          </p>
        </div>
      </div>
    </footer>
  )
}
